import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { apiRequest } from '@/utils/api-request';

export interface PlaygroundConfig {
  prompts: string[];
  default_model: string | null;
}

interface PlaygroundConfigResponse {
  playground?: PlaygroundConfig | null;
}

interface UsePlaygroundConfigResult {
  config: PlaygroundConfig;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  hasFetched: boolean;
  refetch: () => Promise<void>;
  savePrompts: (prompts: string[]) => Promise<PlaygroundConfig>;
  saveDefaultModel: (model: string | null) => Promise<PlaygroundConfig>;
}

const INITIAL_CONFIG: PlaygroundConfig = {
  prompts: [],
  default_model: null,
};

const cache = new Map<string, PlaygroundConfig>();

const buildCacheKey = (orgId?: string, collectionId?: string) => {
  if (!orgId || !collectionId) {
    return '';
  }
  return `${orgId}::${collectionId}`;
};

const normalizeConfig = (
  config?: PlaygroundConfig | null,
): PlaygroundConfig => {
  if (!config) {
    return { ...INITIAL_CONFIG };
  }

  const prompts = Array.isArray(config.prompts)
    ? config.prompts
        .map((prompt) => (typeof prompt === 'string' ? prompt.trim() : ''))
        .filter((prompt) => prompt.length > 0)
    : [];

  return {
    prompts,
    default_model: config.default_model ?? null,
  };
};

export function usePlaygroundConfig(
  orgId?: string,
  collectionId?: string,
): UsePlaygroundConfigResult {
  const cacheKey = useMemo(
    () => buildCacheKey(orgId, collectionId),
    [orgId, collectionId],
  );

  const cachedConfig = cacheKey ? cache.get(cacheKey) : undefined;

  const [config, setConfig] = useState<PlaygroundConfig>(
    cachedConfig ?? INITIAL_CONFIG,
  );
  const configRef = useRef<PlaygroundConfig>(config);
  const [hasFetched, setHasFetched] = useState<boolean>(Boolean(cachedConfig));
  const [isLoading, setIsLoading] = useState<boolean>(
    Boolean(cacheKey) && !cachedConfig,
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  const handleFetch = useCallback(async () => {
    if (!cacheKey) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiRequest<PlaygroundConfigResponse>({
        url: `/collections/${orgId}/${collectionId}/playground`,
        method: 'GET',
        target: 'platform',
      });

      if (response?.data) {
        const normalized = normalizeConfig(response.data.playground);
        cache.set(cacheKey, normalized);
        setConfig(normalized);
        setHasFetched(true);
        return;
      }

      if (response?.detail) {
        setError(response.detail || 'Failed to load playground configuration');
        return;
      }
    } catch (fetchError) {
      const message =
        fetchError instanceof Error
          ? fetchError.message
          : 'Failed to load playground configuration';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [cacheKey, collectionId, orgId]);

  useEffect(() => {
    if (!cacheKey) {
      setConfig(INITIAL_CONFIG);
      configRef.current = INITIAL_CONFIG;
      setHasFetched(false);
      setIsLoading(false);
      return;
    }

    const cached = cache.get(cacheKey);
    if (cached) {
      setConfig(cached);
      configRef.current = cached;
      setHasFetched(true);
      setIsLoading(false);
      return;
    }

    setConfig(INITIAL_CONFIG);
    configRef.current = INITIAL_CONFIG;
    setHasFetched(false);
    setIsLoading(true);
    void handleFetch();
  }, [cacheKey, handleFetch]);

  const updateConfig = useCallback(
    async (payload: Partial<PlaygroundConfig>) => {
      if (!cacheKey) {
        throw new Error('Missing collection context for playground config');
      }

      const previous = configRef.current;
      const optimistic = normalizeConfig({
        ...previous,
        ...payload,
      });

      cache.set(cacheKey, optimistic);
      setConfig(optimistic);
      configRef.current = optimistic;
      setIsSaving(true);
      setError(null);

      try {
        const response = await apiRequest<PlaygroundConfigResponse>({
          url: `/collections/${orgId}/${collectionId}/playground`,
          method: 'PUT',
          target: 'platform',
          body: payload as Record<string, unknown>,
        });

        if (response?.data) {
          const normalized = normalizeConfig(response.data.playground);
          cache.set(cacheKey, normalized);
          setConfig(normalized);
          configRef.current = normalized;
          setError(null);
          return normalized;
        }

        if (response?.detail) {
          throw new Error(
            response.detail || 'Failed to update playground configuration',
          );
        }

        throw new Error(
          'Unexpected response updating playground configuration',
        );
      } catch (mutationError) {
        cache.set(cacheKey, previous);
        setConfig(previous);
        configRef.current = previous;

        const message =
          mutationError instanceof Error
            ? mutationError.message
            : 'Failed to update playground configuration';
        setError(message);
        throw mutationError instanceof Error
          ? mutationError
          : new Error(message);
      } finally {
        setIsSaving(false);
      }
    },
    [cacheKey, collectionId, orgId],
  );

  const savePrompts = useCallback(
    (prompts: string[]) => updateConfig({ prompts }),
    [updateConfig],
  );

  const saveDefaultModel = useCallback(
    (model: string | null) => updateConfig({ default_model: model }),
    [updateConfig],
  );

  return {
    config,
    isLoading,
    isSaving,
    error,
    hasFetched,
    refetch: handleFetch,
    savePrompts,
    saveDefaultModel,
  };
}
